const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/appointment/components/SlotSelector.tsx';
import React, { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import _ from 'lodash';
import { css } from '@emotion/css';
import { Button, Icon, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Theme, useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { colors } from '@cistec/common_web/defs/colors';
import { KeyValuePair, KISIM_STD_DATE_FORMAT, KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import LoadingSpinner from '@cistec/common_web/components/LoadingSpinner';
import { Slot } from 'App/defs/types';
import { SlotsPerDate } from './SlotsPerDate';
import textMap from '../translations';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';

type planSelectorProps = {
    onSlotSelect: (slot: Slot | null) => void;
    requiredMinimumDuration?: number | undefined;
    loadSlots: (startDate: Moment, columnCount: number) => void;
    slots: KeyValuePair[];
    dataLoading?: boolean;
};

const cssSlotSelector = css`
    width: 100%;
`;

const cssNoSlots = (theme: Theme) => css`
    color: ${colors.grey['500']};
    width: 100%;
    margin-top: ${theme.spacing(4)};
    margin-bottom: ${theme.spacing(2)};
    margin-left: ${theme.spacing(2)};
    text-align: center;
`;

const dateRangeButtonPrevious = css`
    border-radius: 4px 0 0 4px;
    min-width: 44px;
    box-shadow: none;
`;

const dateRangeButtonNext = css`
    border-radius: 0 4px 4px 0;
    min-width: 44px;
    box-shadow: none;
`;

const cssDatePickerWrapper = (theme: Theme) => css`
    display: flex;
    margin-bottom: 16px;

    ${theme.breakpoints.down('sm')} {
        justify-content: center;
        min-width: calc(100vw - ${theme.spacing(8)});
    }
`;

const cssLoader = css`
    display: flex;
    justify-content: center;
    width: 100%;
    padding-top: 28px;
`;

export const SlotSelector = (props: planSelectorProps) => {
    const { dataLoading } = props;
    const slots = props.slots ?? [];

    const requiredSlotDuration = props.requiredMinimumDuration ?? 0;
    const config = getConfiguration();
    const { bookAppointmentSlotOffset } = config;
    const { amount, unit } = bookAppointmentSlotOffset;

    const theme = useTheme();
    const { formatMessage } = useIntl();

    const isMediumDevice = useMediaQuery(theme.breakpoints.down('md'));
    const columnCount: number = isMediumDevice ? 1 : 5;

    const firstAvailableTime =
        unit === 'hours' ? moment().add(amount, unit) : moment().add(Math.max(amount, 1), unit).startOf('day');
    const firstAvailableDate = firstAvailableTime.clone().startOf('day');

    const [loadedDateRange, setLoadedDateRange] = useState<Moment[]>([]);

    const [amountOfDaysToLoad, setAmountOfDaysToLoad] = useState(columnCount);

    const [selectedDate, setSelectedDate] = useState<Moment>(firstAvailableDate);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

    let firstRender = false;

    useEffect(() => {
        firstRender = true;
    }, []);

    useEffect(() => {
        const fromDate = selectedDate.isSame(firstAvailableTime, 'day')
            ? firstAvailableTime
            : selectedDate.startOf('day');
        const endDate = fromDate.clone().add(amountOfDaysToLoad - 1, 'days');
        if (fromDate.isBefore(loadedDateRange[0], 'day') || endDate.isAfter(loadedDateRange[1], 'day')) {
            setLoadedDateRange([fromDate, endDate]);
            props.loadSlots(fromDate, amountOfDaysToLoad);
        }
    }, [selectedDate, amountOfDaysToLoad]);

    useEffect(() => {
        if (firstRender) return;

        setAmountOfDaysToLoad(columnCount);

        if (selectedSlot) {
            const slotDate = moment(selectedSlot.startDate);
            if (columnCount === 1) {
                if (!slotDate.isSame(selectedDate, 'day')) {
                    setSelectedDate(slotDate);
                }
            } else {
                if (!slotDate.isSame(loadedDateRange[0], 'day')) {
                    setSelectedDate(loadedDateRange[0]);
                }
            }
        }
    }, [columnCount]);

    useEffect(() => {
        if (firstRender) return;

        props.onSlotSelect(selectedSlot);
    }, [selectedSlot]);

    const slotList = slots.map(
        (slot: KeyValuePair): Slot => ({
            id: slot.Id,
            startDate: slot.STARTDAT,
            endDate: slot.STOPDAT,
            schedule: slot.Schedule,
        })
    );

    const filterByDuration = (slot: Slot) => {
        const slotDuration = moment(slot.endDate, KISIM_TIME_FORMAT).diff(
            moment(slot.startDate, KISIM_TIME_FORMAT),
            'minutes'
        );
        return slotDuration >= requiredSlotDuration;
    };

    const filterUniqueTimes = (slot: Slot, index: number, array: Slot[]) => {
        const findSlotWithSameTime = (precedingSlot: Slot) => precedingSlot.startDate === slot.startDate;
        return !array.slice(0, index).find(findSlotWithSameTime);
    };
    const filteredSlotList = slotList.filter(filterByDuration).filter(filterUniqueTimes);

    const slotsGroupedByStartDate = _.groupBy(_.orderBy(filteredSlotList, 'startDate', 'asc'), (slot: Slot) => {
        return moment(slot.startDate, KISIM_TIME_FORMAT).format(KISIM_STD_DATE_FORMAT);
    });

    // slot id's are not unique, every api call returns new id for the same slot
    // so we find our “selectedSlot” based on the start and end date and set this new  :(
    // This is relevant when only one column is loaded, and the screen is resized so more
    // columns are supposed to be shown and more data is loaded
    useEffect(() => {
        if (selectedSlot) {
            const findMatchingSlot = (slot: Slot) =>
                slot.startDate === selectedSlot.startDate && slot.endDate === selectedSlot.endDate;

            // || null to set selected slot to null when find returns undefined
            const newSelectedSlot = filteredSlotList.find(findMatchingSlot) || null;

            setSelectedSlot(newSelectedSlot);
        }
    }, [slots]);

    useEffect(() => {
        if (selectedSlot) {
            const slotDate = moment(selectedSlot.startDate);
            if (columnCount === 1) {
                if (!slotDate.isSame(selectedDate, 'day')) {
                    setSelectedSlot(null);
                }
            } else {
                if (slotDate.isBefore(loadedDateRange[0], 'day') || slotDate.isAfter(loadedDateRange[1], 'day')) {
                    setSelectedSlot(null);
                }
            }
        }
    }, [selectedDate]);

    const handlePreviousClick = (_event: any) => {
        const newDate = selectedDate.clone().subtract(columnCount, 'days');
        setSelectedDate(newDate.isSameOrAfter(firstAvailableDate) ? newDate : firstAvailableDate);
    };

    const handleNextClick = (_event: any) => {
        const newDate = selectedDate.clone().add(columnCount, 'days');
        setSelectedDate(newDate);
    };

    const getContent = () => {
        if (dataLoading) {
            return (
                <div className={cssLoader}>
                    <LoadingSpinner />
                </div>
            );
        } else if (filteredSlotList.length === 0) {
            return (
                <Typography className={cssNoSlots(theme)} align={'left'}>
                    {formatMessage(textMap.noAvailableSlots)}
                </Typography>
            );
        } else {
            const slotsPerDays: ReactNode[] = [];

            const handleSlotsPerDateChange = (slot: Slot) => {
                setSelectedSlot(slot);
            };
            for (let i = 0; i < columnCount; i++) {
                const dayDate = selectedDate.clone().add(i, 'days');
                const date = dayDate.format(KISIM_STD_DATE_FORMAT);
                slotsPerDays.push(
                    <SlotsPerDate
                        key={date}
                        date={date}
                        selectedSlot={selectedSlot}
                        slots={slotsGroupedByStartDate}
                        onChange={handleSlotsPerDateChange}
                    />
                );
            }
            return slotsPerDays;
        }
    };

    return (
        <div className={cssSlotSelector}>
            <div className={cssDatePickerWrapper(theme)}>
                <Button
                    className={dateRangeButtonPrevious}
                    disabled={!selectedDate.isAfter(firstAvailableDate, 'day')}
                    color={'secondary'}
                    variant={'text'}
                    size={'small'}
                    onClick={handlePreviousClick}
                >
                    <Icon className={'icons8-chevron-left'} component="span" />
                </Button>
                <DatePicker
                    format={KISIM_STD_DATE_FORMAT}
                    minDate={firstAvailableDate}
                    value={selectedDate}
                    onChange={(newValue: any) => {
                        setSelectedDate(newValue);
                    }}
                    slotProps={{
                        textField: { variant: 'outlined' },
                    }}
                />
                <Button
                    className={dateRangeButtonNext}
                    color={'secondary'}
                    variant={'text'}
                    size={'small'}
                    onClick={handleNextClick}
                >
                    <Icon className={'icons8-chevron-right'} component="span" />
                </Button>
            </div>
            <Grid container spacing={2}>
                {getContent()}
            </Grid>
        </div>
    );
};
